/**
 * Test Run Service
 * Handles test run-related database operations
 */

import { COLLECTIONS, SCHEMAS } from "../config.js";
import { buildApiHeaders, resolveInternalApiUrl } from "@/utils/apiHeaders";

import { BaseService } from "./BaseService.js";

export class TestRunService extends BaseService {
  constructor() {
    super(COLLECTIONS.TEST_RUNS);
  }

  /**
   * Get the next available test run ID
   * @returns {Promise<string>} Next test run ID (e.g., TR00001)
   */
  async getNextTestRunId() {
    try {
      // Use backend batch endpoint (count=1) to generate ID atomically
      // Avoids fetching all test runs just to find the max ID
      const ids = await this.getNextTestRunIds(1);
      if (Array.isArray(ids) && ids.length > 0) {
        return ids[0];
      }
      // Fallback to timestamp-based ID
      return `TR${Date.now().toString().slice(-5)}`;
    } catch (error) {
      // Fallback to timestamp-based ID if there's an error
      return `TR${Date.now().toString().slice(-5)}`;
    }
  }

  /**
   * Get multiple next available test run IDs (batch generation to avoid race conditions)
   * @param {number} count - Number of IDs to generate
   * @returns {Promise<string[]>} Array of test run IDs
   */
  async getNextTestRunIds(count) {
    try {
      await this._ensureDatabaseService();

      // Use backend batch endpoint to generate IDs atomically
      // The adapter's makeRequest method handles the base URL
      if (this.db && this.db.makeRequest) {
        return await this.db.makeRequest(`/test_runs/next-ids/${count}`, {
          method: "GET",
        });
      } else {
        // Fallback: use fetch directly
        const url = resolveInternalApiUrl(`/test_runs/next-ids/${count}`);
        const response = await fetch(url, {
          method: "GET",
          headers: buildApiHeaders({
            url,
            includeAuth: true,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to get test run IDs: ${response.statusText}`);
        }

        return await response.json();
      }
    } catch (error) {
      // Fallback: generate IDs sequentially
      const ids = [];
      for (let i = 0; i < count; i++) {
        ids.push(await this.getNextTestRunId());
      }
      return ids;
    }
  }

  /**
   * Assign test run IDs to existing test runs that don't have one
   * @returns {Promise<number>} Number of test runs updated
   */
  async assignTestRunIds() {
    const allTestRuns = await this.getAll();
    const testRunsWithoutId = allTestRuns.filter((tr) => !tr.testrun_id);

    let updatedCount = 0;
    for (const testRun of testRunsWithoutId) {
      const testRunId = await this.getNextTestRunId();
      await this.update(testRun.id, { testrun_id: testRunId });
      updatedCount++;
    }

    return updatedCount;
  }

  /**
   * Bulk create multiple test runs in a single operation
   * Uses MongoDB insertMany for optimal performance
   * @param {Array<Object>} testRunsData - Array of test run data objects
   * @returns {Promise<Array>} Array of created test runs
   */
  async bulkCreateTestRuns(testRunsData) {
    if (!testRunsData || testRunsData.length === 0) {
      return [];
    }

    const { http } = await import("@/utils/httpClient");
    const response = await http.post("/test_runs/bulk-create", {
      testRuns: testRunsData,
    });

    return response;
  }

  /**
   * Create a new test run
   * @param {Object} testRunData - Test run data
   * @returns {Promise<Object>} Created test run
   */
  async createTestRun(testRunData) {
    const serviceRequestId = `svc_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Generate ID if not provided
    if (!testRunData.id) {
      testRunData.id = `test_runs_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    }

    // Set required timestamp fields before validation
    const now = new Date();
    if (!testRunData.created_at) {
      testRunData.created_at = now;
    }
    if (!testRunData.updated_at) {
      testRunData.updated_at = now;
    }

    // Set default status if not provided
    if (!testRunData.status) {
      testRunData.status = "YET_TO_START";
    }

    // Initialize test cases array if not provided
    if (!testRunData.test_cases) {
      testRunData.test_cases = [];
    }

    // Ensure ETA is properly formatted (convert ISO string to Date if needed for validation)
    // But keep as ISO string for actual data since backend expects Date which Mongoose will parse
    if (testRunData.eta) {
      // If it's already an ISO string, keep it (backend Mongoose will parse it)
      // If it's a Date object, convert to ISO string
      if (testRunData.eta instanceof Date) {
        testRunData.eta = testRunData.eta.toISOString();
      } else if (typeof testRunData.eta === "string") {
        // Already an ISO string, validate it's parseable
        const parsed = new Date(testRunData.eta);
        if (isNaN(parsed.getTime())) {
          throw new Error("Invalid ETA date format");
        }
      }
    }

    // Validate after setting required fields
    const validation = this.validateData(
      testRunData,
      SCHEMAS[COLLECTIONS.TEST_RUNS]
    );

    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    const result = await this.create(testRunData);
    return result;
  }

  /**
   * Update test run
   * @param {string} testRunId - Test run ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated test run
   */
  async updateTestRun(testRunId, updateData) {
    // Ensure ETA is properly formatted (convert ISO string to Date if needed for validation)
    // But keep as ISO string for actual data since backend expects Date which Mongoose will parse
    if (updateData.eta !== undefined && updateData.eta !== null) {
      // If it's already an ISO string, keep it (backend Mongoose will parse it)
      // If it's a Date object, convert to ISO string
      if (updateData.eta instanceof Date) {
        updateData.eta = updateData.eta.toISOString();
      } else if (typeof updateData.eta === "string") {
        // Already an ISO string, validate it's parseable
        const parsed = new Date(updateData.eta);
        if (isNaN(parsed.getTime())) {
          throw new Error("Invalid ETA date format");
        }
      }
    }

    const validation = this.validateData(
      updateData,
      SCHEMAS[COLLECTIONS.TEST_RUNS]
    );

    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
    }

    return await this.update(testRunId, updateData);
  }

  /**
   * Get test runs by project (excludes deleted by default)
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test runs
   */
  async getByProject(projectId, options = {}) {
    // Get all test runs for this project using base service method
    // Backend already filters is_deleted: { $ne: true } — no client-side filter needed
    return await this.findBy("project_id", projectId, options);
  }

  /**
   * Get test runs by status
   * @param {string} status - Test run status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test runs
   */
  async getByStatus(status, options = {}) {
    return await this.findBy("status", status, options);
  }

  /**
   * Get test runs by assigned user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test runs
   */
  async getByAssignedUser(userId, options = {}) {
    return await this.findBy("assigned_to", userId, options);
  }

  /**
   * Get test runs by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of test runs
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy("created_by", userId, options);
  }

  /**
   * Get active test runs
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of active test runs
   */
  async getActive(options = {}) {
    return await this.getByStatus("running", options);
  }

  /**
   * Get completed test runs
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of completed test runs
   */
  async getCompleted(options = {}) {
    return await this.getByStatus("completed", options);
  }

  /**
   * Start test run
   * @param {string} testRunId - Test run ID
   * @returns {Promise<Object>} Updated test run
   */
  async start(testRunId) {
    const updateData = {
      status: "running",
      started_at: new Date(),
    };
    return await this.update(testRunId, updateData);
  }

  /**
   * Complete test run
   * @param {string} testRunId - Test run ID
   * @returns {Promise<Object>} Updated test run
   */
  async complete(testRunId) {
    const updateData = {
      status: "completed",
      completed_at: new Date(),
    };
    return await this.update(testRunId, updateData);
  }

  /**
   * Cancel test run
   * @param {string} testRunId - Test run ID
   * @returns {Promise<Object>} Updated test run
   */
  async cancel(testRunId) {
    const updateData = {
      status: "cancelled",
      completed_at: new Date(),
    };
    return await this.update(testRunId, updateData);
  }

  /**
   * Search test runs
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching test runs
   */
  async searchTestRuns(searchTerm, options = {}) {
    const searchOptions = {
      fields: ["name", "description"],
      ...options,
    };
    return await this.search(searchTerm, searchOptions);
  }

  /**
   * Get test run statistics
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise<Object>} Test run statistics
   */
  async getStatistics(projectId = null) {
    let testRuns;
    if (projectId) {
      testRuns = await this.getByProject(projectId);
    } else {
      testRuns = await this.getAll();
    }

    const statusCounts = {};
    const environmentCounts = {};
    let totalTestCases = 0;

    testRuns.forEach((testRun) => {
      // Count by status
      statusCounts[testRun.status] = (statusCounts[testRun.status] || 0) + 1;

      // Count by environment
      if (testRun.environment) {
        environmentCounts[testRun.environment] =
          (environmentCounts[testRun.environment] || 0) + 1;
      }

      // Count total test cases
      totalTestCases += testRun.test_cases.length;
    });

    return {
      total: testRuns.length,
      statusCounts,
      environmentCounts,
      totalTestCases,
      averageTestCasesPerRun:
        testRuns.length > 0
          ? Math.round((totalTestCases / testRuns.length) * 100) / 100
          : 0,
      lastUpdated: new Date(),
    };
  }

  /**
   * Get recent test runs
   * @param {number} limit - Number of recent runs to return
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise<Array>} Array of recent test runs
   */
  async getRecent(limit = 10, projectId = null) {
    const options = {
      orderBy: [this._orderBy("created_at", "desc")],
      limit,
    };

    if (projectId) {
      return await this.getByProject(projectId, options);
    } else {
      return await this.getAll(options);
    }
  }

  /**
   * Get test run trends
   * @param {string} projectId - Project ID (optional)
   * @param {number} days - Number of days to look back
   * @returns {Promise<Array>} Array of trend data
   */
  async getTrends(projectId = null, days = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const conditions = [this._where("created_at", ">=", cutoffDate)];

    if (projectId) {
      conditions.push(this._where("project_id", "==", projectId));
    }

    const testRuns = await this.findWhere(conditions, {
      orderBy: [this._orderBy("created_at", "asc")],
    });

    // Group by date
    const trends = {};
    testRuns.forEach((testRun) => {
      const date = new Date(testRun.created_at).toISOString().split("T")[0];
      if (!trends[date]) {
        trends[date] = {
          date,
          total: 0,
          completed: 0,
          running: 0,
          cancelled: 0,
        };
      }
      trends[date].total++;
      trends[date][testRun.status] = (trends[date][testRun.status] || 0) + 1;
    });

    return Object.values(trends);
  }

  /**
   * Add activity to test run
   * Uses backend append endpoint to avoid sending full activities array.
   * @param {string} testRunId - Test run ID
   * @param {Object} activity - Activity data
   * @returns {Promise<Object>} Updated test run
   */
  async addActivity(testRunId, activity) {
    const activityData = {
      id: activity.id || Date.now() + Math.random(),
      ...activity,
      timestamp: activity.timestamp || new Date(),
    };
    const { http } = await import("@/utils/httpClient");
    return await http.post(`/test_runs/${testRunId}/activities`, activityData);
  }

  /**
   * Update a single test case in the test_cases array.
   * Uses optimized backend endpoint that only updates the specific test case.
   * Backend recalculates progress and status automatically.
   * @param {string} testRunId - Test run ID
   * @param {string} testCaseId - Test case ID
   * @param {Object} updateData - Update data (only changed fields)
   * @returns {Promise<Object>} Updated test run
   */
  async updateTestCase(testRunId, testCaseId, updateData) {
    const { http } = await import("@/utils/httpClient");
    return await http.patch(
      `/test_runs/${testRunId}/test-cases/${testCaseId}`,
      updateData
    );
  }

  /**
   * Add and/or remove many test cases on a run in one request.
   * Sends ID lists only — the server $pulls / $pushes without rewriting the full array.
   */
  async bulkSyncTestCases(
    testRunId,
    payload: {
      add?: Array<
        | string
        | {
            testCaseId: string;
            folderPath?: string | null;
            assigned_to?: string | null;
          }
      >;
      remove?: string[];
      metadata?: Record<string, unknown>;
    } = {}
  ) {
    const { http } = await import("@/utils/httpClient");
    const body: {
      add: Array<
        | string
        | {
            testCaseId: string;
            folderPath?: string | null;
            assigned_to?: string | null;
          }
      >;
      remove: string[];
      metadata?: Record<string, unknown>;
    } = {
      add: payload.add || [],
      remove: payload.remove || [],
    };
    if (payload.metadata) {
      body.metadata = payload.metadata;
    }
    return await http.patch(
      `/test_runs/${testRunId}/test-cases/membership`,
      body
    );
  }

  /**
   * Add a single test case to a test run (membership bulk with one id).
   */
  async addTestCase(testRunId, testCaseId) {
    return await this.bulkSyncTestCases(testRunId, { add: [testCaseId] });
  }

  /**
   * Remove a single test case from a test run (membership bulk with one id).
   */
  async removeTestCase(testRunId, testCaseId) {
    return await this.bulkSyncTestCases(testRunId, { remove: [testCaseId] });
  }

  /**
   * Bulk update multiple test cases in the test_cases array.
   * Uses optimized backend endpoint that only updates the specified test cases.
   * Backend recalculates progress and status automatically.
   * @param {string} testRunId - Test run ID
   * @param {Array} updates - Array of { testCaseId, updateData } objects
   * @returns {Promise<Object>} Minimal response with only updated test cases
   */
  async bulkUpdateTestCases(testRunId, updates) {
    const { http } = await import("@/utils/httpClient");
    return await http.patch(`/test_runs/${testRunId}/test-cases/bulk`, {
      updates,
    });
  }

  /**
   * Get activities for test run
   * @param {string} testRunId - Test run ID
   * @returns {Promise<Array>} Array of activities
   */
  async getActivities(testRunId) {
    const testRun = await this.getById(testRunId);
    if (!testRun) {
      throw new Error("Test run not found");
    }

    return testRun.activities || [];
  }

  /**
   * Add comment to test run
   * @param {string} testRunId - Test run ID
   * @param {Object} comment - Comment data with text, userName, userId, timestamp
   * @returns {Promise<Object>} Updated test run
   */
  async addComment(testRunId, comment) {
    const testRun = await this.getById(testRunId);
    if (!testRun) {
      throw new Error("Test run not found");
    }

    const userId = comment.userId || comment.user_id || "unknown";
    const userName = comment.userName || comment.user_name || "Unknown User";

    const newComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      user_id: userId,
      user_name: userName,
      comment: comment.text || comment.comment || "",
      timestamp: comment.timestamp || new Date(),
      created_at: comment.created_at || new Date(),
    };

    const comments = Array.isArray(testRun.comments)
      ? [...testRun.comments]
      : [];
    comments.push(newComment);

    const activities = testRun.activities || [];
    activities.push({
      type: "comment_added",
      description: `${userName} added a comment`,
      userName: userName,
      user_name: userName,
      userId: userId,
      user_id: userId,
      timestamp: new Date(),
      created_at: new Date(),
      details: {
        commentId: newComment.id,
        commentPreview: (newComment.comment || "").substring(0, 100),
      },
    });

    return await this.update(testRunId, {
      comments: comments,
      activities: activities,
      updated_at: new Date(),
    });
  }

  /**
   * Get comments for test run
   * @param {string} testRunId - Test run ID
   * @returns {Promise<Array>} Array of comments
   */
  async getComments(testRunId) {
    const testRun = await this.getById(testRunId);
    if (!testRun) {
      throw new Error("Test run not found");
    }

    return Array.isArray(testRun.comments) ? testRun.comments : [];
  }

  /**
   * Update an existing comment on a test run and record a `comment_updated` activity.
   * @param {string} testRunId
   * @param {string} commentId
   * @param {string} newCommentText
   * @param {string} userId
   * @param {string} userName
   * @returns {Promise<Object>} Updated test run
   */
  async updateComment(testRunId, commentId, newCommentText, userId, userName) {
    const testRun = await this.getById(testRunId);
    if (!testRun) {
      throw new Error("Test run not found");
    }

    const comments = Array.isArray(testRun.comments)
      ? [...testRun.comments]
      : [];
    const commentIndex = comments.findIndex((c) => c.id === commentId);
    if (commentIndex === -1) {
      throw new Error("Comment not found");
    }

    comments[commentIndex] = {
      ...comments[commentIndex],
      comment: newCommentText,
      edited_at: new Date(),
      is_edited: true,
    };

    const activities = Array.isArray(testRun.activities)
      ? [...testRun.activities]
      : [];
    activities.push({
      type: "comment_updated",
      description: `${userName || "A user"} updated a comment`,
      userName: userName,
      user_name: userName,
      userId: userId,
      user_id: userId,
      timestamp: new Date(),
      created_at: new Date(),
      details: {
        commentId: commentId,
        commentPreview: (newCommentText || "").substring(0, 100),
      },
    });

    return await this.update(testRunId, {
      comments: comments,
      activities: activities,
      updated_at: new Date(),
    });
  }

  /**
   * Delete a comment from a test run and record a `comment_deleted` activity.
   * @param {string} testRunId
   * @param {string} commentId
   * @param {string} userId
   * @param {string} userName
   * @returns {Promise<Object>} Updated test run
   */
  async deleteComment(testRunId, commentId, userId, userName) {
    const testRun = await this.getById(testRunId);
    if (!testRun) {
      throw new Error("Test run not found");
    }

    const comments = (
      Array.isArray(testRun.comments) ? testRun.comments : []
    ).filter((c) => c.id !== commentId);

    const activities = Array.isArray(testRun.activities)
      ? [...testRun.activities]
      : [];
    activities.push({
      type: "comment_deleted",
      description: `${userName || "A user"} deleted a comment`,
      userName: userName,
      user_name: userName,
      userId: userId,
      user_id: userId,
      timestamp: new Date(),
      created_at: new Date(),
      details: {
        commentId: commentId,
      },
    });

    return await this.update(testRunId, {
      comments: comments,
      activities: activities,
      updated_at: new Date(),
    });
  }

  /**
   * Get test runs with filters
   * @param {Object} filters - Filter options
   * @param {string} [filters.projectId] - Project ID filter
   * @param {string} [filters.status] - Status filter
   * @param {string} [filters.assigneeId] - Assignee ID filter
   * @param {string} [filters.createdById] - Created by ID filter
   * @param {string} [filters.search] - Search term
   * @param {number} [filters.page] - Page number
   * @param {number} [filters.pageSize] - Page size
   * @returns {Promise<Object>} Paginated results with data and total
   */
  async getTestRunsWithFilters(
    filters: {
      projectId?: string;
      status?: string;
      assigneeId?: string;
      createdById?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {}
  ) {
    await this._ensureDatabaseService();

    const conditions = [];

    // Add filters
    if (filters.projectId) {
      const condition = this._where("project_id", "==", filters.projectId);
      conditions.push(condition);
    }

    if (filters.status) {
      const condition = this._where("status", "==", filters.status);
      conditions.push(condition);
    }

    if (filters.assigneeId) {
      const condition = this._where("assigned_to", "==", filters.assigneeId);
      conditions.push(condition);
    }

    if (filters.createdById) {
      const condition = this._where("created_by", "==", filters.createdById);
      conditions.push(condition);
    }

    if (filters.search) {
      // For search, we'll need to implement text search
      // For now, we'll search in name and description fields
      const condition = this._where("name", "contains", filters.search);
      conditions.push(condition);
    }

    const options: {
      orderBy: any[];
      limit?: number;
      offset?: number;
    } = {
      orderBy: [this._orderBy("created_at", "desc")],
    };

    // Add pagination if provided
    if (filters.page && filters.pageSize) {
      const offset = (filters.page - 1) * filters.pageSize;
      options.limit = filters.pageSize;
      options.offset = offset;
    }

    const data = await this.db.query(this.collectionName, conditions, options);

    // Get total count with the same conditions but without pagination
    const totalData = await this.db.query(this.collectionName, conditions, {});
    const total = totalData.length;

    return {
      data,
      total,
      page: filters.page || 1,
      pageSize: filters.pageSize || 20,
      totalPages: Math.ceil(total / (filters.pageSize || 20)),
    };
  }

  /**
   * Soft delete a test run
   * @param {string} testRunId - Test run ID
   * @param {string} deletedBy - User ID who deleted the test run
   * @returns {Promise<Object>} Updated test run
   */
  async softDelete(testRunId, deletedBy) {
    // Use dedicated soft-delete endpoint (bypasses update() safeguard)
    const { http } = await import("@/utils/httpClient");
    const response = await http.post(`/test_runs/${testRunId}/soft-delete`, {
      deletedBy,
    });
    return response;
  }

  /**
   * Restore a soft-deleted test run
   * @param {string} testRunId - Test run ID
   * @param {string} restoredBy - User ID who restored the test run
   * @returns {Promise<Object>} Updated test run
   */
  async restore(testRunId, restoredBy) {
    const updateData = {
      is_deleted: false,
      deleted_at: null,
      deleted_by: null,
    };

    // Add activity for restoration
    try {
      await this.addActivity(testRunId, {
        type: "test_run_restored",
        description: "Test run was restored",
        userName: restoredBy,
        details: {
          testRunId: testRunId,
        },
      });
    } catch (activityError) {}

    return await this.update(testRunId, updateData);
  }

  /**
   * Get deleted test runs by project
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of deleted test runs
   */
  async getDeletedByProject(projectId) {
    // Get all test runs for this project using base service method
    const allTestRuns = await this.findBy("project_id", projectId);

    // Filter to only deleted test runs
    const deletedTestRuns = allTestRuns.filter(
      (testRun) => testRun.is_deleted === true
    );

    return deletedTestRuns;
  }

  /**
   * Get multiple test runs by their IDs (batched in a single request)
   * @param {Array<string>} testRunIds - Array of test run IDs
   * @returns {Promise<Array>} Array of test runs
   */
  async getTestRunsByIds(testRunIds, feild = "testrun_id") {
    if (!Array.isArray(testRunIds) || testRunIds.length === 0) {
      return [];
    }

    return await this.findByIn(feild, testRunIds, { excludeDeleted: true });
  }
}

export const testRunService = new TestRunService();
